import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePasswordSchema, type ChangePasswordInput } from '@rhpc/shared';
import { AuthBrand } from '@/components/BrandMark';
import { Button, Input } from '@rhpc/ui';
import { useAuthStore } from '@/stores/authStore';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [serverError, setServerError] = useState<string | null>(null);
  // Captured once on mount: forced first-login flow vs. a voluntary change from the profile page.
  const [isForced] = useState(() => !!user?.mustChangePassword);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: ChangePasswordInput) => {
    setServerError(null);
    try {
      const response = await fetch('/api/v1/auth/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) {
        setServerError(result.error?.message ?? 'Unable to change password');
        return;
      }
      if (user) {
        useAuthStore.setState({
          user: { ...user, mustChangePassword: false },
        });
      }
      if (isForced) {
        navigate('/dashboard');
      } else {
        navigate('/profile', { state: { flash: 'Password updated successfully' } });
      }
    } catch {
      setServerError('Network error. Please try again.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-8">
      <div className="w-full max-w-[392px]">
        <AuthBrand
          title="Change password"
          subtitle={isForced ? 'Required before first use' : 'Choose a new password for your account'}
        />
        <div className="rounded-lg border border-border bg-surface p-8 shadow-sm">
          {serverError && (
            <div className="mb-4 rounded-md border-l-[3px] border-l-danger bg-danger-soft p-3" role="alert">
              <p className="text-sm text-danger">{serverError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <Input
              id="currentPassword"
              label={isForced ? 'Temporary password' : 'Current password'}
              type="password"
              autoComplete="current-password"
              error={errors.currentPassword?.message}
              {...register('currentPassword')}
            />
            <Input
              id="newPassword"
              label="New password"
              type="password"
              autoComplete="new-password"
              error={errors.newPassword?.message}
              {...register('newPassword')}
            />
            <Input
              id="confirmPassword"
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
            <Button type="submit" loading={isSubmitting} disabled={isSubmitting} className="mt-2 w-full">
              Save password
            </Button>
            {!isForced && (
              <Button
                type="button"
                variant="secondary"
                disabled={isSubmitting}
                className="w-full"
                onClick={() => navigate('/profile')}
              >
                Cancel
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
